import ReactDOM from 'react-dom';
import VariableEditorPopup, { VAR_TOOLTIP_Z } from '../VariableEditorPopup';
import { clipboardMenuItems } from '../../../utils/clipboardMenu';
import ContextMenu from '../ContextMenu';
import OsScroll from '../OsScroll';

/** The input markup with tooltip, variable editor, selection menu and suggestions. Plain function called during render, so the element tree is unchanged. */
export function renderSyntaxHighlightInput({
  t,
  overlayRect,
  className,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariables,
  folderVariables,
  onVariableUpdate,
  variableContext,
  style,
  expandOnOverflow,
  tooltip,
  tooltipRef,
  tooltipPlace,
  isFocused,
  editingVariable,
  setEditingVariable,
  selectionMenu,
  dynSuggestions,
  setDynSuggestions,
  isExpanded,
  setEditableRef,
  anchorRef,
  overlayRef,
  editingVariableRef,
  isKvField,
  closeSelectionMenu,
  saveVariableValue,
  openToVariableEditor,
  insertDynVar,
  sharedEditableProps,
}) {
  return (
    <div ref={anchorRef} className="relative inline-block w-full syntax-highlight-input">
      {/* Contenteditable div for input with syntax highlighting */}
      {expandOnOverflow && isExpanded ? (
        // Reserves the exact box the collapsed field occupied (captured at expand time) so the
        // row (method dropdown, Send/History buttons) never resizes while the floating overlay
        // below is open — using the real measured height rather than an assumed constant avoids
        // any mismatch shifting the content below the URL bar.
        // Keyed apart from the editable below: without distinct keys React REUSES the
        // editable's <div> for this placeholder, and the text typed into it (DOM the
        // contentEditable owns, not React) stays inside — hidden, but still laid out.
        // A long unbroken value then overflowed the Params cell by ~50px and gave the
        // whole request pane a sideways scroll (28.09.2026).
        <div key="expanded-placeholder" aria-hidden="true" style={{ height: overlayRect?.height ?? (isKvField ? 36 : 44), visibility: 'hidden' }} />
      ) : (
        <div
          key="editable"
          ref={setEditableRef}
          {...sharedEditableProps}
          className={`${className || ''} ${isFocused && !isKvField ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}`}
          style={{
            minHeight: isKvField ? '36px' : '44px',
            outline: 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            // Ellipsis only at rest. While editing, a click at the end scrolls the
            // field to the caret, and Chromium keeps painting the ellipsis where
            // the text was cut, so the visible part of the value went blank
            // (Variables panel, owner 16.10.2026). Clip scrolls cleanly.
            textOverflow: isFocused ? 'clip' : 'ellipsis',
            display: isKvField ? 'block' : 'flex',
            alignItems: isKvField ? undefined : 'center',
            width: '100%',
            minWidth: '100%',
            flex: isKvField ? undefined : '1',
            lineHeight: isKvField ? '22px' : undefined,
            ...style
          }}
        />
      )}

      {/* URL field only: floating multi-line overlay, positioned over the anchor so it never
          shifts surrounding layout. Closes back to the collapsed single-line field on blur. */}
      {expandOnOverflow && isExpanded && overlayRect && ReactDOM.createPortal(
        <div
          ref={overlayRef}
          className="syntax-highlight-input-overlay"
          style={{ top: overlayRect.top, left: overlayRect.left, width: overlayRect.width }}
        >
          <div
            ref={setEditableRef}
            {...sharedEditableProps}
            className={`${className || ''} syntax-highlight-input__expanded panel-scroll`}
            style={{
              outline: 'none',
              // Chromium/WebKit force pre-wrap-like line handling on contentEditable regions
              // internally for editing purposes regardless of this value, so `normal` here
              // wouldn't actually change anything — kept as `pre-wrap` to match reality.
              // Some WebViews can surface that internal wrapping as real `\n` characters in
              // textContent/clipboard; that's sanitized out at the data layer in handleInput/
              // handleBlur instead (only the intended soft-wrap display is ever kept here).
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              // Scroll past the CSS max-height (~5 lines) instead of spilling the text outside the
              // box: a bare `overflow: visible` here would override the stylesheet's overflow-y:auto
              // (inline beats a non-!important rule), so the capped height had no scrollbar. The
              // variable tooltip/editor popups are portaled to <body>, so clipping here can't hide
              // them. overflow-x stays hidden — long tokens wrap via word-break above.
              overflowY: 'auto',
              overflowX: 'hidden',
              width: '100%',
              minWidth: '100%',
            }}
          />
        </div>,
        document.body
      )}

      {/* Variable Tooltip */}
      {tooltip && !editingVariable && ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          className="variable-tooltip fixed pointer-events-none"
          /* Above the whole editor stack — a hint drawn behind a nested editor
             is a hint you cannot read — and inside the window, which drawing it
             at the raw cursor position was not: a chip near the right edge put
             its own explanation off-screen. */
          style={{
            left: tooltipPlace?.left ?? tooltip.x + 14,
            top: tooltipPlace?.top ?? tooltip.y + 16,
            zIndex: VAR_TOOLTIP_Z,
          }}
        >
          {tooltip.exists ? (
            <>
              <div className="variable-tooltip-top">
                <span className="variable-tooltip-name">{`{{${tooltip.name}}}`}</span>
                <span className="variable-tooltip-value">{tooltip.value}</span>
              </div>
              <div className="variable-tooltip-meta">
                <span className="variable-tooltip-scope-badge">
                  {tooltip.folderName ? `${tooltip.type} · ${tooltip.folderName}` : tooltip.type}
                </span>
                {!tooltip.isDynamic && (
                  <span className="variable-tooltip-hint">· click to edit</span>
                )}
              </div>
            </>
          ) : (
            <div className="variable-tooltip-top">
              <span className="variable-tooltip-name">{`{{${tooltip.name}}}`}</span>
              <span className="variable-tooltip-missing">Undefined</span>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Variable suggestions: the user's own first, the generated `$` ones below.

          The fixed-position panel and the scroller are TWO elements on purpose.
          OverlayScrollbars sets `position: relative` on whatever element it is
          given, which silently overrode this panel's `position: fixed` — `top`
          then measured from the end of <body> instead of the viewport and the
          list rendered a thousand pixels below the field it belongs to
          (owner, 23.09.2026). The scroller goes INSIDE the positioned box. */}
      {dynSuggestions && ReactDOM.createPortal(
        <div
          className="dyn-var-suggestions"
          style={{ left: dynSuggestions.x, top: dynSuggestions.y }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <OsScroll className="dyn-var-suggestions__scroll" options={{ overflow: { x: 'hidden' } }}>
            {dynSuggestions.items.map((item, i) => (
              <div
                key={`${item.kind}:${item.name}`}
                className={`dyn-var-suggestion-item dyn-var-suggestion-item--${item.kind}`
                  + (i === dynSuggestions.active ? ' dyn-var-suggestion-item--active' : '')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertDynVar(item, dynSuggestions);
                }}
                onMouseEnter={() => setDynSuggestions((prev) => prev ? { ...prev, active: i } : null)}
              >
                <span className="dyn-var-suggestion-name">{item.name}</span>
                <span className="dyn-var-suggestion-desc">{item.detail}</span>
                {item.kind === 'saved' && <span className="dyn-var-suggestion-scope">{item.scope}</span>}
              </div>
            ))}
          </OsScroll>
        </div>,
        document.body
      )}

      {/* "To Variable" context menu. z-index above the expanded URL overlay (100000), which the
          ContextMenu default (z-50) would otherwise sit under. */}
      {selectionMenu && (
        <ContextMenu
          x={selectionMenu.x}
          y={selectionMenu.y}
          zIndex={100001}
          className="shi-selection-menu"
          items={[
            // The browser's menu is suppressed to show this one, so it has to
            // bring cut/copy/paste back — losing them to gain one action is a
            // bad trade, and it is why this menu used to be URL-only.
            { label: t('request.toVariable'), onClick: openToVariableEditor, disabled: !selectionMenu.canExtract },
            ...clipboardMenuItems({
              hasSelection: selectionMenu.hasSelection,
              canPaste: selectionMenu.canPaste,
              onDone: closeSelectionMenu,
              t,
            }),
          ]}
          onClose={closeSelectionMenu}
        />
      )}

      {/* Variable Editor */}
      {editingVariable && (
        <VariableEditorPopup
          /* Keyed per variable. The popup seeds value/name/scope with useState, which runs
             ONLY on mount — and clicking a second variable while the first editor is open
             swaps `editingVariable` without unmounting (saveVariableValue documents exactly
             that race). Without a key React reuses the instance, so the popup kept the FIRST
             variable's value and name: clicking away then wrote A's value under B's name, or
             compared A's text against B's initial value, decided "nothing changed" and
             silently discarded what had just been typed. */
          key={`${editingVariable.name || ''}|${editingVariable.type || ''}|${editingVariable.nameEditable ? 'n' : ''}`}
          editingVariable={editingVariable}
          variableContext={variableContext}
          selectedEnvironment={selectedEnvironment}
          workspaceVariables={workspaceVariables}
          globalVariables={globalVariables}
          collectionVariables={collectionVariables}
          folderVariables={folderVariables}
          onVariableUpdate={onVariableUpdate}
          onSave={saveVariableValue}
          onClose={() => { editingVariableRef.current = null; setEditingVariable(null); }}
        />
      )}
    </div>
  );
}
